import CONFIG from "Config";

/**
 * platform-api session bootstrap for the eai to platform-api cutover.
 *
 * Migrated modules call platform-api, which is a different host from eai-api, so
 * they need a platform-api session cookie of their own: eai's cookie is scoped
 * to eai's host and will never be sent there. Rather than widen that cookie to
 * `.enflux.com` or keep a FusionAuth token alive in JS, we exchange the access
 * token the frontend already holds at login for platform-api's own host-scoped
 * HttpOnly cookie. The generated client then rides that cookie
 * (`credentials: "include"`) while `EAIClient` keeps using eai's, so the two are
 * host-selective by construction.
 *
 * Deliberately plain `fetch` rather than the generated client: the generated
 * client treats 401 and 431 as a dead session and redirects to login, which
 * would turn a failed login exchange into a redirect during login.
 *
 * Every call here is best-effort. A platform-api failure must never block or
 * slow the primary eai auth flow, and everything is a no-op until platform-api
 * is configured (`CONFIG.apis.newApi`).
 */

const AUTH_BASE = "/api/v1/auth";

/** Whether a platform-api session should be maintained at all. */
export function isPlatformApiConfigured(): boolean {
    return Boolean(CONFIG.apis?.newApi);
}

function postPlatformApi(path: string, body?: unknown): Promise<Response> {
    const headers: Record<string, string> = {};

    if (body !== undefined) {
        headers["Content-Type"] = "application/json";
    }

    return fetch(`${CONFIG.apis?.newApi ?? ""}${path}`, {
        method: "POST",
        credentials: "include",
        headers,
        body: body === undefined ? undefined : JSON.stringify(body)
    });
}

async function establish(path: string, body: unknown, label: string): Promise<boolean> {
    if (!isPlatformApiConfigured()) {
        return false;
    }

    try {
        const response = await postPlatformApi(path, body);

        if (!response.ok) {
            console.warn(`[platform-session] ${label} failed: HTTP ${response.status}`);
            return false;
        }

        return true;
    } catch (error) {
        console.warn(`[platform-session] ${label} error:`, error);
        return false;
    }
}

/**
 * Exchange the access token from an SSO login for a platform-api session
 * cookie. Resolves true on success, false when platform-api is unconfigured or
 * the exchange fails.
 */
export function establishPlatformApiSession(params: {
    accessToken: string;
    schoolId: string;
    email?: string | null;
}): Promise<boolean> {
    return establish(`${AUTH_BASE}/login`, {
        accessToken: params.accessToken,
        schoolId: params.schoolId,
        email: params.email ?? undefined
    }, "login");
}

/**
 * Establish a platform-api session from an email and password login.
 * platform-api authenticates against FusionAuth itself for this path.
 */
export function establishPlatformApiSessionWithCredentials(params: {
    email: string;
    password: string;
    schoolId: string;
}): Promise<boolean> {
    return establish(`${AUTH_BASE}/login/credentials`, {
        email: params.email,
        password: params.password,
        schoolId: params.schoolId
    }, "credentials login");
}

/**
 * Refresh the platform-api session cookie alongside eai's. Without this the
 * platform cookie expires while the eai session is still being renewed, and
 * migrated modules start failing partway through a long-lived tab.
 */
export async function refreshPlatformApiSession(): Promise<void> {
    if (!isPlatformApiConfigured()) {
        return;
    }

    try {
        await postPlatformApi(`${AUTH_BASE}/refresh`);
    } catch (error) {
        console.warn("[platform-session] refresh error:", error);
    }
}

/** Clear the platform-api session cookie on logout. Best-effort. */
export async function clearPlatformApiSession(): Promise<void> {
    if (!isPlatformApiConfigured()) {
        return;
    }

    try {
        await postPlatformApi(`${AUTH_BASE}/logout`);
    } catch {
        // Logout must not fail because platform-api is unreachable.
    }
}
